import {Select} from 'antd';
import React, {useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {I18nSimpleKey} from '../../../src/i18n/i18n';
import {MapStatsWithRegion, RegionSubset} from '../../../src/map-stats';
import {GadmLevel, GetMapStatsRow, getMapStats} from '../../../src/models/interfaces';
import {DbFilterState} from '../../../src/redux/reducers/filters';
import {getCountryCodeGroups} from '../../../src/selectors/units';
import {chunk, remove} from '../../../src/util/arr-util';
import {getPostgrestQueryParams} from '../../../src/util/postgrest-query';
import {filtersToRequest} from '../../../src/util/req-util';
import {useApis} from '../apis/ApisContext';
import {COUNTRIES_BY_GADM_LEVEL} from '../map/useMapStatsLayer';

export const regionLevelLabels: Record<GadmLevel, null | I18nSimpleKey> = {
  '0': 'RegionLevel_country',
  '1': 'RegionLevel_state',
  '2': null,
  '3': null,
  '4': null,
  '5': 'RegionLevel_commune',
};

export function useRegionData() {
  const {authedFetcher} = useApis();

  const fetchRegionDetails = useCallback(
    async (mapStats: GetMapStatsRow[]) => {
      if (mapStats.length == 0) {
        return {};
      }
      // Making a get request for all regions is causing 431 Request Header Fields Too Large error.
      // So we are splitting the request into chunks of 300 regions.
      const chunks = chunk(mapStats, 300);
      const requests: Promise<RegionSubset[]>[] = chunks.map(chunk => {
        const params: [string, string][] = [
          ['select', 'gadm_level,name,region_id,region_info'],
          ...getPostgrestQueryParams({
            column: 'region_id',
            operator: 'in',
            value: chunk.map(row => row.region_id).filter(remove.nulls),
          }),
        ];
        return authedFetcher({
          method: 'GET',
          path: 'api/regions',
          params,
        });
      });
      const chunkedRegions = await Promise.all(requests);
      return Object.fromEntries<RegionSubset>(chunkedRegions.flat().map<[string, RegionSubset]>(r => [r.region_id, r]));
    },
    [authedFetcher],
  );

  const getRegionsData = useCallback(
    async (filters: DbFilterState, gadmLevel: GadmLevel) => {
      const mapStats = await getMapStats(authedFetcher, {...filtersToRequest(filters), gadm_level: gadmLevel});
      const regionIdDetails = await fetchRegionDetails(mapStats.filter(row => row.region_id));
      const mapStatsRowWithName: MapStatsWithRegion[] = mapStats.map(row => {
        const regionDetails = row.region_id ? regionIdDetails[row.region_id] : null;
        return {...row, region: regionDetails};
      });
      return {
        mapStats,
        mapStatsRowWithName,
      };
    },
    [authedFetcher, fetchRegionDetails],
  );

  return {fetchRegionDetails, getRegionsData};
}

function getGranularGadmLevelForCountry(countryCode: string): null | GadmLevel {
  for (const [gadmLevel, countries] of Object.entries(COUNTRIES_BY_GADM_LEVEL)) {
    if (countries.includes(countryCode)) {
      return gadmLevel as GadmLevel;
    }
  }
  return null;
}

export function getAvailableRegionLevels(countryGroups: string[]): GadmLevel[] {
  const levels = new Set<GadmLevel>();
  for (const countryGroup of countryGroups) {
    if (countryGroup == 'FRA') {
      levels.add('0');
      levels.add('1');
      levels.add('2');
      levels.add('5');
    } else {
      const maxLevel = getGranularGadmLevelForCountry(countryGroup);
      for (let i = 0; i <= parseInt(maxLevel ?? ''); ++i) {
        levels.add(String(i) as GadmLevel);
      }
    }
  }

  return Array.from(levels);
}

interface RegionLevelSelectorProps {
  selectedRegionLevel: GadmLevel;
  onSelectedRegionLevelChanged: (regionLevel: GadmLevel) => void;
  placeholder?: string;
  style?: React.CSSProperties;
}

export const RegionLevelSelector: React.FC<RegionLevelSelectorProps> = ({
  selectedRegionLevel,
  onSelectedRegionLevelChanged,
  placeholder,
  style,
}) => {
  const {t} = useApis();
  const countryGroups = useSelector(getCountryCodeGroups);
  const availableRegionLevels = useMemo(() => getAvailableRegionLevels(countryGroups), [countryGroups]);

  // TODO(savv): deprecate this in favor of the following logic for the region selector:
  //  If the user has access to only one country:
  //  - FRA: Show "Country, Region, Department, Municipality" (levels 1, 2, 5)
  //  - BRA: Show "Country, State, Municipality" (levels 1, 2)
  //  - ITA: Show "Country, Region, Province, Municipality" (levels 1, 2, 3)
  //  If the user has access to more than one country:
  //  Show levels with country context, for example (FRA, BRA):
  //  - Level 1: Country, Region (FRA), State (BRA)
  //  - Level 2: Country, Department (FRA), Municipality (BRA)
  //  - Level 5: Country, Municipality (FRA)
  //  When that happens `getRegionsData` should be inlined into useQuery(['getRegionsData', ...], ...);
  const regionLevelOptions = useMemo(
    () => [
      ...(availableRegionLevels ?? []).map(r => ({
        value: r,
        label: `${regionLevelLabels[r] ? t(regionLevelLabels[r]!) : `${t('RegionLevel')}: ${r}`}`,
      })),
    ],
    [t, availableRegionLevels],
  );

  return (
    <Select
      options={regionLevelOptions}
      value={selectedRegionLevel}
      onChange={onSelectedRegionLevelChanged}
      placeholder={placeholder ?? t('RegionLevel')}
      style={style}
    />
  );
};

export default RegionLevelSelector;
